import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  InteractionRequiredAuthError,
  type PublicClientApplication,
} from '@azure/msal-browser';
import {
  getAccessTokenAsync,
  refreshAccessToken,
  clearAllAuth,
} from './authentication';
import { getAccessToken as getDevToken, logout as legacyLogout } from './authService';
import { getConfig } from './config';

/**
 * API client for general (non-auth) ClinicMgmt endpoints.
 *
 * Token resolution order (in the request interceptor):
 *   1. SSO/legacy via getAccessTokenAsync — handles MSAL silent renewal
 *      for SSO users and reads the legacy bearer from localStorage 'auth'
 *      for password-login users
 *   2. In-memory dev token from authService — only set by the dev/sandbox
 *      login buttons (DEV mode only)
 *
 * baseURL is resolved lazily inside the interceptor: main.tsx awaits
 * loadConfig() before render, but module bodies evaluate earlier in the
 * import chain, so getConfig() at module scope would throw.
 *
 * 401 handling mirrors httpClientFactory: a single in-flight refresh-token
 * request is shared across concurrent failures via a queue. Refresh failure
 * (or no refresh-token id, e.g. SSO users whose MSAL token was rejected)
 * triggers a full sign-out and redirect to /login.
 */

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null): void {
  for (const prom of failedQueue) {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  }
  failedQueue = [];
}

function clearAllAuthAndRedirect(): void {
  legacyLogout();
  clearAllAuth();
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
}

/**
 * Force-logout an SSO user when MSAL says interaction is required.
 *
 * Also wipes the MSAL account cache — without that, LoginPage would see
 * `getAllAccounts().length > 0` on the next mount and retry
 * `acquireTokenSilent()` instead of starting a fresh `loginRedirect()`,
 * leaving the user stuck on /login.
 */
async function clearSsoAndRedirect(msalInstance: PublicClientApplication): Promise<void> {
  try {
    await msalInstance.clearCache();
  } catch {
    // Best-effort: still clear app auth state and redirect.
  }
  clearAllAuthAndRedirect();
}

const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(async (requestConfig) => {
  const cfg = getConfig();
  requestConfig.baseURL = cfg.apiBase;

  let token: string | null = null;
  try {
    token = await getAccessTokenAsync(cfg.scope);
  } catch {
    token = null;
  }
  if (!token) {
    token = getDevToken();
  }
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const url = originalRequest.url ?? '';
    const isAuthEndpoint =
      url.includes('/login') ||
      url.includes('/refresh-token') ||
      url.includes('/revoke-token') ||
      url.includes('/dev/token');
    const isLoginPage =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/login');

    if (isAuthEndpoint || isLoginPage) {
      return Promise.reject(error);
    }

    // During session restoration the in-memory token may not be populated yet;
    // a 401 here is expected — let it pass instead of clearing localStorage
    // and triggering a redirect loop.
    const isAuthLoading = sessionStorage.getItem('__auth_loading') === '1';
    if (isAuthLoading) {
      return Promise.reject(error);
    }

    // ── SSO path ──
    // SSO users have no legacy refreshTokenId — running them through the
    // refresh-token flow throws "No refresh token available", which used to
    // clearAllAuth() and bounce the user straight back to /login on every
    // post-login 401. Instead try a forced MSAL silent acquisition and retry
    // the request; only clear+redirect when MSAL itself says we need to re-
    // authenticate interactively. Transient MSAL/network failures just
    // propagate the 401 — the user's session may still be recoverable.
    if (localStorage.getItem('ssoSession')) {
      originalRequest._retry = true;
      const { getInitializedMsalInstance } = await import('./msalInstance');
      const { loginRequest } = await import('./msalConfig');
      const cfg = getConfig();

      let msalInstance: PublicClientApplication;
      try {
        msalInstance = await getInitializedMsalInstance();
      } catch (initErr) {
        // MSAL failed to initialize — propagate without nuking the session.
        return Promise.reject(initErr);
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        // SSO session in localStorage but no MSAL account — inconsistent
        // state. Treat as interaction-required.
        await clearSsoAndRedirect(msalInstance);
        return Promise.reject(error);
      }

      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest(cfg.scope, accounts[0].username),
          account: accounts[0],
          forceRefresh: true,
        });
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (silentErr) {
        if (silentErr instanceof InteractionRequiredAuthError) {
          await clearSsoAndRedirect(msalInstance);
        }
        // Transient / non-interaction errors: keep the session and surface
        // the 401 so callers can show a contextual error.
        return Promise.reject(silentErr);
      }
    }

    // ── Legacy refresh-token path ──
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { token } = await refreshAccessToken();
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      processQueue(null, token);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAllAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
